<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { get } from 'svelte/store';
  import { createMutation } from '@tanstack/svelte-query';

  import Button from '$components/ui/button.svelte';
  import Input from '$components/ui/input.svelte';
  import Link from '$components/ui/link.svelte';

  import { t } from '$lib/i18n';
  import { shopify } from '$lib/shopify';
  import { toaster } from '$lib/toaster';
  import {
    CUSTOMER_USER_ERROR_CAUSE,
    handleCustomerUserErrors,
    type ParsedCustomerUserError
  } from '$lib/utils/shopify/handleCustomerError';

  import type {
    CustomerRecoverMutation,
    CustomerRecoverMutationVariables
  } from '$gql/storefront/operations';
  import { drawer } from '$lib/stores/drawer';
  import { AUTH_DRAWER_IDS } from '$lib/constants';
  import Icon from '$components/ui/icon/icon.svelte';
  import { aboveLg } from '$lib/stores/breakpoints';

  /* GraphQL */ `
    mutation customerRecover($email: String!) {
      customerRecover(email: $email) {
        customerUserErrors {
          code
          field
          message
        }
      }
    }
  `;

  const dispatch = createEventDispatcher<{ success: undefined }>();
  export let lastSentResetEmail: string | undefined;

  $: recover = createMutation<
    CustomerRecoverMutation,
    ParsedCustomerUserError,
    CustomerRecoverMutationVariables
  >({
    mutationFn: async ({ email }) => {
      const res = await $shopify.customerRecover({ email });

      // User error handling
      const customerError =
        res.customerRecover?.customerUserErrors &&
        handleCustomerUserErrors(res.customerRecover?.customerUserErrors);
      if (customerError) {
        throw customerError;
      }

      return res;
    },
    onSuccess: (_, { email }) => {
      lastSentResetEmail = email;
      form?.reset();
      dispatch('success');
    },
    onError: (err) => {
      if (err?.cause === CUSTOMER_USER_ERROR_CAUSE) {
        err.errors.forEach((err) => toaster({ type: 'error', title: err.message }));
      } else {
        toaster({ type: 'error', title: get(t)('auth.server-error') });
      }
    }
  });

  let form: HTMLFormElement;
</script>

<div class="flex justify-between">
  <div>
    <h2 class="text-4xl font-semibold mb-2">{$t('auth.reset.title')}</h2>
    <p class="text-md">
      {$t('auth.reset.description')}
    </p>
  </div>
  {#if $aboveLg}
    <button name="close" on:click={() => drawer.close()} class="block lg:hidden">
      <Icon icon="cross" size="lg" />
    </button>
  {/if}
</div>

<form
  bind:this={form}
  on:submit={(e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const { email } = Object.fromEntries(data.entries());

    $recover.mutateAsync({
      email: email.toString()
    });
  }}
  class="mt-11"
>
  <Input
    type="email"
    id="email"
    name="email"
    label={$t('auth.reset.email-label')}
    placeholder={$t('auth.reset.email-placeholder')}
    autocomplete="email"
    required
    class="mb-5"
  />

  <Button
    name="reset"
    loading={$recover.isLoading}
    disabled={$recover.isLoading}
    size="md"
    type="submit"
    class="w-full"
  >
    {$t('auth.reset.button')}
  </Button>

  <p class="mt-5 text-center text-sm">
    {$t('auth.reset.cta')}
    <Link
      as="button"
      class="font-semibold text-sm"
      on:click={() => {
        drawer.open(AUTH_DRAWER_IDS.LOGIN);
      }}
    >
      {$t('auth.reset.cta-action')}
    </Link>
  </p>
</form>
